import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Camera, Loader2, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { compressImage } from "@/lib/imageUtils";

export const RequireProfilePhoto = () => {
  const { user, refreshProfile } = useAuth();
  const [preview, setPreview] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    e.target.value = "";
  };

  const handleSave = async () => {
    if (!file) {
      toast.error("Selecione uma foto primeiro.");
      return;
    }
    if (!user) return;

    setLoading(true);
    try {
      // Compress the image before uploading to prevent freezing on large files
      const compressedFile = await compressImage(file, 800, 0.7);
      
      const fileExt = compressedFile.name.split(".").pop() || "jpg";
      const path = `${user.id}/avatar-${Date.now()}.${fileExt}`;

      let uploadRes = await supabase.storage
        .from("avatars")
        .upload(path, compressedFile, { upsert: true, cacheControl: "3600" });

      if (uploadRes.error) {
        console.warn("Avatar upload fallback to media bucket:", uploadRes.error.message);
        uploadRes = await supabase.storage
          .from("media")
          .upload(`avatars/${path}`, compressedFile, { upsert: true, cacheControl: "3600" });
      }

      if (uploadRes.error) throw uploadRes.error;

      const isMediaBucket = uploadRes.data?.path?.startsWith("avatars/");
      const bucketName = isMediaBucket ? "media" : "avatars";
      const { data: publicData } = supabase.storage.from(bucketName).getPublicUrl(uploadRes.data?.path || path);
      const avatarUrl = `${publicData.publicUrl}?t=${Date.now()}`;

      const { error: updateError } = await supabase
        .from("profiles")
        .update({ avatar_url: avatarUrl })
        .eq("user_id", user.id);

      if (updateError) throw updateError;

      toast.success("Foto de perfil salva com sucesso!");
      await refreshProfile();
    } catch (err: any) {
      console.error("Erro ao salvar foto de perfil:", err);
      toast.error(err.message || "Erro ao salvar a foto de perfil.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-xl flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="w-full max-w-md bg-[#111] border border-red-600/30 rounded-[32px] overflow-hidden shadow-[0_30px_60px_-12px_rgba(220,38,38,0.4)]"
      >
        <div className="p-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-black text-white mb-2">FOTO DE PERFIL OBRIGATÓRIA 📸</h2>
            <p className="text-sm text-white/80 font-medium bg-red-500/20 p-3 rounded-xl border border-red-500/30">
              Para continuar, você precisa enviar uma <strong className="text-white">foto de perfil clara que mostre bem o seu rosto</strong>. Isso é obrigatório para a sua identificação e segurança nos álbuns.
            </p>
          </div>

          <div
            className="w-36 h-36 mx-auto rounded-full bg-white/5 border-4 border-white/10 flex items-center justify-center overflow-hidden mb-6 cursor-pointer hover:border-red-600/50 transition-colors relative"
            onClick={() => inputRef.current?.click()}
          >
            {preview ? (
              <img src={preview} alt="Preview" className="w-full h-full object-cover" />
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Camera className="w-10 h-10 text-white/30" />
                <span className="text-[10px] text-white/40 font-bold uppercase">Toque aqui</span>
              </div>
            )}
            <div className="absolute inset-0 bg-black/30 opacity-0 hover:opacity-100 flex items-center justify-center transition-opacity rounded-full">
              <Camera className="w-8 h-8 text-white" />
            </div>
          </div>

          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
          />

          {!preview && (
            <button
              onClick={() => inputRef.current?.click()}
              className="w-full border-2 border-dashed border-white/20 rounded-2xl py-6 text-sm font-bold text-white/40 hover:border-red-600/50 hover:text-white/60 transition-colors mb-6 flex items-center justify-center gap-2"
            >
              <Camera className="w-5 h-5" />
              Selecionar Foto da Galeria
            </button>
          )}

          {preview && (
            <button
              onClick={() => inputRef.current?.click()}
              className="w-full text-center text-xs text-white/40 hover:text-white/60 transition-colors mb-4 underline"
            >
              Trocar foto
            </button>
          )}

          <Button
            onClick={handleSave}
            disabled={loading || !file}
            className="w-full h-14 rounded-xl text-lg font-black bg-red-600 text-white hover:bg-red-700 transition-all disabled:opacity-40"
          >
            {loading ? (
              <Loader2 className="w-6 h-6 animate-spin" />
            ) : (
              <>
                <CheckCircle className="w-5 h-5 mr-2" />
                Salvar e Continuar
              </>
            )}
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

export default RequireProfilePhoto;
